import {
  Box,
  Button,
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import AppPaperTitle from 'src/app/common/components/AppPaperTitle';
import { PruTableHeader } from 'src/app/common/components/Table/PruTableHeader';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'src/app/common/hook/translation.hook';
import useEventCallback from 'src/app/common/hook/useEventCallback';
import { DEFAULT_ROWS_PER_PAGE_OPTIONS } from 'src/app/modules/OpmPd/constants/common.constant';
import { formatDateTime } from 'src/app/modules/OpmPd/utils/commonHelper';
import TableHeaderFilterLabel from '../../../../../../../common/components/TableHeaderFilterLabel';
import { usePoliciesApprovalRequestPageContext } from '../../PoliciesApprovalRequestPage.context';
import useStyles from './PolicyTable.styles';
import ExportButton from 'src/app/modules/OpmPd/components/ExportButton/ExportButton';
import _isEqual from 'lodash/isEqual';

const EMPTY_FILTER_VALUE = '(Empty)';

const withEmpty = (sorted: string[]): string[] => [EMPTY_FILTER_VALUE, ...sorted];

const defaultColumnFilters: Record<ColumnKey, string[]> = {
  policyNumber: [],
  policyStatusCode: [],
  currentAgentCode: [],
  soldUnit: [],
  importedAgent: [],
  suggestedAgent: [],
  _otherQualifiedAgent: [],
  selectedAgent: [],
  selectedAgentName: [],
  isFirstPrioritySelectedAgent: [],
  reasonSelectedAnotherAgent: [],
  actionedExpireAt: [],
  _policyTaskStatusLabel: [],
};

type ColumnKey =
  | 'policyNumber'
  | 'policyStatusCode'
  | 'currentAgentCode'
  | 'soldUnit'
  | 'importedAgent'
  | 'suggestedAgent'
  | '_otherQualifiedAgent'
  | 'selectedAgent'
  | 'selectedAgentName'
  | 'isFirstPrioritySelectedAgent'
  | 'reasonSelectedAnotherAgent'
  | 'actionedExpireAt'
  | '_policyTaskStatusLabel';

const PolicyTable = () => {
  const t = useTranslation();
  const { classes } = useStyles();

  const { lmBatchFile, policies, selectedPolicyIds, validPolicyIds, setSelectedPolicyIds } =
    usePoliciesApprovalRequestPageContext();

  const [columnFilters, setColumnFilters] = useState<Record<ColumnKey, string[]>>(defaultColumnFilters);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE_OPTIONS[0] ?? DEFAULT_ROWS_PER_PAGE_OPTIONS[0]);

  const [filteredItems, setFilteredItems] = useState([...policies]);

  const columnOptions = useMemo<Record<ColumnKey, string[]>>(
    () => ({
      policyNumber: withEmpty([...new Set(policies.map((i) => String(i.policyNumber ?? '')))].filter(Boolean).sort()),
      policyStatusCode: withEmpty(
        [...new Set(policies.map((i) => String(i.policyStatusCode ?? '')) ?? [])].filter(Boolean).sort(),
      ),
      currentAgentCode: withEmpty(
        [...new Set(policies.map((i) => String(i.currentAgentCode ?? '')) ?? [])].filter(Boolean).sort(),
      ),
      soldUnit: withEmpty([...new Set(policies.map((i) => String(i.soldUnit ?? '')) ?? [])].filter(Boolean).sort()),
      importedAgent: withEmpty(
        [...new Set(policies.map((i) => String(i.importedAgent ?? '')) ?? [])].filter(Boolean).sort(),
      ),
      suggestedAgent: withEmpty(
        [...new Set(policies.map((i) => String(i.suggestedAgent ?? '')) ?? [])].filter(Boolean).sort(),
      ),
      _otherQualifiedAgent: withEmpty(
        [...new Set(policies.map((i) => String(i._otherQualifiedAgent ?? '')) ?? [])].filter(Boolean).sort(),
      ),
      selectedAgent: withEmpty(
        [...new Set(policies.map((i) => String(i.selectedAgent ?? '')) ?? [])].filter(Boolean).sort(),
      ),
      selectedAgentName: withEmpty(
        [...new Set(policies.map((i) => String(i.selectedAgentName ?? '')) ?? [])].filter(Boolean).sort(),
      ),
      isFirstPrioritySelectedAgent: withEmpty(
        [...new Set(policies.map((i) => (i.isFirstPrioritySelectedAgent ? '✓' : '✕')) ?? [])].filter(Boolean).sort(),
      ),
      reasonSelectedAnotherAgent: withEmpty(
        [...new Set(policies.map((i) => String(i.reasonSelectedAnotherAgent ?? '')) ?? [])].filter(Boolean).sort(),
      ),
      _policyTaskStatusLabel: withEmpty(
        [...new Set(policies.map((i) => String(i._policyTaskStatusLabel ?? '')) ?? [])].filter(Boolean).sort(),
      ),
      actionedExpireAt: withEmpty(
        [...new Set(policies.map((i) => String(i.actionedExpireAt ?? '')) ?? [])].filter(Boolean).sort(),
      ),
    }),
    [policies],
  );

  const changeFilteredItems = useEventCallback((payload?: { columnFilters: typeof columnFilters }) => {
    const { columnFilters } = payload || {};

    const matchStr = (filter: string[] | null | undefined, val: string | null | undefined): boolean => {
      if (['null', 'undefined'].includes(typeof filter) || !filter?.length) return true;
      const s = String(val ?? '');
      return filter!.some((f) => (f === EMPTY_FILTER_VALUE ? !s : f === s));
    };
    setFilteredItems(
      policies.filter(
        (item) =>
          matchStr(columnFilters?.policyNumber, item.policyNumber) &&
          matchStr(columnFilters?.policyStatusCode, item.policyStatusCode) &&
          matchStr(columnFilters?.currentAgentCode, item.currentAgentCode) &&
          matchStr(columnFilters?.soldUnit, item.soldUnit) &&
          matchStr(columnFilters?.importedAgent, item.importedAgent) &&
          matchStr(columnFilters?.suggestedAgent, item.suggestedAgent) &&
          matchStr(columnFilters?.selectedAgent, item.selectedAgent) &&
          matchStr(columnFilters?.selectedAgentName, item.selectedAgentName) &&
          matchStr(columnFilters?.isFirstPrioritySelectedAgent, item.isFirstPrioritySelectedAgent ? '✓' : '✕') &&
          matchStr(columnFilters?.reasonSelectedAnotherAgent, item.reasonSelectedAnotherAgent) &&
          matchStr(columnFilters?._policyTaskStatusLabel, item._policyTaskStatusLabel) &&
          matchStr(columnFilters?.actionedExpireAt, item.actionedExpireAt),
      ),
    );
  });

  const makeFilterHandler = (col: ColumnKey) => (selected: string[]) => {
    setPage(0);
    const validSelected = selected.filter((s) => columnOptions[col].includes(s));
    const newColumnFilters = {
      ...columnFilters,
      [col]: validSelected.length === 0 || validSelected.length === columnOptions[col].length ? [] : validSelected,
    };
    setColumnFilters(newColumnFilters);
    changeFilteredItems({ columnFilters: newColumnFilters });
  };

  const paginatedItems = useMemo(
    () => filteredItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredItems, page, rowsPerPage],
  );

  const checkboxAllIndeterminate = useMemo(() => {
    const tempPaginatedItems = paginatedItems.filter((item) => validPolicyIds.includes(item.id));
    return (
      tempPaginatedItems.some((item) => selectedPolicyIds.includes(item.id)) &&
      !tempPaginatedItems.every((item) => selectedPolicyIds.includes(item.id))
    );
  }, [paginatedItems, selectedPolicyIds]);

  const checkboxAllChecked = useMemo(() => {
    const tempPaginatedItems = paginatedItems.filter((item) => validPolicyIds.includes(item.id));
    return tempPaginatedItems.every((item) => selectedPolicyIds.includes(item.id)) && tempPaginatedItems.length > 0;
  }, [paginatedItems, selectedPolicyIds]);

  const handleChangePage = (_: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleToggleRow = (policyId: string) => {
    setSelectedPolicyIds((prev) =>
      prev.includes(policyId) ? prev.filter((id) => id !== policyId) : [...prev, policyId],
    );
  };

  const handleToggleAllOnPage = () => {
    const tempPaginatedItems = paginatedItems.filter((item) => validPolicyIds.includes(item.id));
    const pagePolicyIds = tempPaginatedItems.map((item) => item.id);
    const allSelected = pagePolicyIds.every((policyId) => selectedPolicyIds.includes(policyId));
    if (allSelected) {
      setSelectedPolicyIds((prev) => prev.filter((policyId) => !pagePolicyIds.includes(policyId)));
    } else {
      setSelectedPolicyIds((prev) => [...new Set([...prev, ...pagePolicyIds])]);
    }
  };

  const handleClearFilters = () => {
    setColumnFilters(defaultColumnFilters);
    changeFilteredItems({ columnFilters: defaultColumnFilters });
    setPage(0);
  };

  const paginatedItemPolicyIds = useMemo(() => {
    return paginatedItems?.map((paginatedItem) => String(paginatedItem.id)) || [];
  }, [paginatedItems]);
  const paginatedItemPolicyIdsRef = useRef(paginatedItemPolicyIds);

  useEffect(() => {
    if (_isEqual(paginatedItemPolicyIdsRef.current, paginatedItemPolicyIds)) {
      return;
    }
    setSelectedPolicyIds([]);
    paginatedItemPolicyIdsRef.current = paginatedItemPolicyIds;
  }, [paginatedItemPolicyIds]);

  return (
    <Paper elevation={0} variant="elevation" className={classes.root}>
      <AppPaperTitle>
        {lmBatchFile?.batchNo} - {t('opm_pd.policyList')} -{' '}
        {selectedPolicyIds.length ? `(${t('opm_pd.policySelected', { count: selectedPolicyIds.length })})` : ''}
        <Box display="flex" flex={1} alignItems="center" justifyContent="flex-end" gap={1}>
          <ExportButton module="lmBatchFilePolicies" batchId={lmBatchFile?.id} />
          <Button
            variant="outlined"
            disabled={Object.values(columnFilters).every((v) => v.length === 0)}
            onClick={handleClearFilters}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {t('opm_pd.clearFilters')}
          </Button>
        </Box>
      </AppPaperTitle>
      <div>
        <TableContainer sx={{ maxHeight: `calc(100dvh - 220px)` }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <PruTableHeader padding="checkbox">
                  <Checkbox
                    checked={checkboxAllChecked}
                    indeterminate={checkboxAllIndeterminate}
                    onChange={handleToggleAllOnPage}
                    sx={{
                      color: 'white',
                      '&.Mui-checked': { color: 'white' },
                      '&.MuiCheckbox-indeterminate': { color: 'white' },
                    }}
                  />
                </PruTableHeader>
                <PruTableHeader>
                  <TableHeaderFilterLabel
                    label={t('opm_pd.policyNumber')}
                    options={columnOptions.policyNumber}
                    selectedOptions={columnFilters.policyNumber ?? []}
                    onSelectionChange={makeFilterHandler('policyNumber')}
                  />
                </PruTableHeader>
                <PruTableHeader>
                  <TableHeaderFilterLabel
                    label={t('opm_pd.policyStatusCode')}
                    options={columnOptions.policyStatusCode}
                    selectedOptions={columnFilters.policyStatusCode ?? []}
                    onSelectionChange={makeFilterHandler('policyStatusCode')}
                  />
                </PruTableHeader>
                <PruTableHeader>
                  <TableHeaderFilterLabel
                    label={t('opm_pd.currentAgentCode')}
                    options={columnOptions.currentAgentCode}
                    selectedOptions={columnFilters.currentAgentCode ?? []}
                    onSelectionChange={makeFilterHandler('currentAgentCode')}
                  />
                </PruTableHeader>
                <PruTableHeader>
                  <TableHeaderFilterLabel
                    label={t('opm_pd.soldUnit')}
                    options={columnOptions.soldUnit}
                    selectedOptions={columnFilters.soldUnit ?? []}
                    onSelectionChange={makeFilterHandler('soldUnit')}
                  />
                </PruTableHeader>
                <PruTableHeader>
                  <TableHeaderFilterLabel
                    label={t('opm_pd.importedAgent')}
                    options={columnOptions.importedAgent}
                    selectedOptions={columnFilters.importedAgent ?? []}
                    onSelectionChange={makeFilterHandler('importedAgent')}
                  />
                </PruTableHeader>
                <PruTableHeader>
                  <TableHeaderFilterLabel
                    label={t('opm_pd.suggestedAgent')}
                    options={columnOptions.suggestedAgent}
                    selectedOptions={columnFilters.suggestedAgent ?? []}
                    onSelectionChange={makeFilterHandler('suggestedAgent')}
                  />
                </PruTableHeader>
                <PruTableHeader>
                  <TableHeaderFilterLabel
                    label={t('opm_pd.otherQualifiedAgent')}
                    options={columnOptions._otherQualifiedAgent}
                    selectedOptions={columnFilters._otherQualifiedAgent ?? []}
                    onSelectionChange={makeFilterHandler('_otherQualifiedAgent')}
                  />
                </PruTableHeader>
                <PruTableHeader>
                  <TableHeaderFilterLabel
                    label={t('opm_pd.selectedAgent')}
                    options={columnOptions.selectedAgent}
                    selectedOptions={columnFilters.selectedAgent ?? []}
                    onSelectionChange={makeFilterHandler('selectedAgent')}
                  />
                </PruTableHeader>
                <PruTableHeader>
                  <TableHeaderFilterLabel
                    label={t('opm_pd.selectedAgentName')}
                    options={columnOptions.selectedAgentName}
                    selectedOptions={columnFilters.selectedAgentName ?? []}
                    onSelectionChange={makeFilterHandler('selectedAgentName')}
                  />
                </PruTableHeader>
                <PruTableHeader align="center">
                  <TableHeaderFilterLabel
                    align="center"
                    label={t('opm_pd.is1stPriority')}
                    options={columnOptions.isFirstPrioritySelectedAgent}
                    selectedOptions={columnFilters.isFirstPrioritySelectedAgent ?? []}
                    onSelectionChange={makeFilterHandler('isFirstPrioritySelectedAgent')}
                  />
                </PruTableHeader>
                <PruTableHeader>
                  <TableHeaderFilterLabel
                    label={t('opm_pd.reasonForSelectingAnotherAgent')}
                    options={columnOptions.reasonSelectedAnotherAgent}
                    selectedOptions={columnFilters.reasonSelectedAnotherAgent ?? []}
                    onSelectionChange={makeFilterHandler('reasonSelectedAnotherAgent')}
                  />
                </PruTableHeader>
                <PruTableHeader>
                  <TableHeaderFilterLabel
                    label={t('opm_pd.status')}
                    options={columnOptions._policyTaskStatusLabel}
                    selectedOptions={columnFilters._policyTaskStatusLabel ?? []}
                    onSelectionChange={makeFilterHandler('_policyTaskStatusLabel')}
                  />
                </PruTableHeader>
                <PruTableHeader align="right">
                  <TableHeaderFilterLabel
                    align="right"
                    label={t('opm_pd.deadlineForLm')}
                    options={columnOptions.actionedExpireAt}
                    selectedOptions={columnFilters.actionedExpireAt ?? []}
                    onSelectionChange={makeFilterHandler('actionedExpireAt')}
                  />
                </PruTableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedItems.map((item, index) => {
                return (
                  <TableRow key={item.id ?? index} hover selected={selectedPolicyIds.includes(item.id)}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedPolicyIds.includes(item.id)}
                        disabled={!validPolicyIds.includes(item.id)}
                        onChange={() => handleToggleRow(item.id)}
                      />
                    </TableCell>
                    <TableCell>{item.policyNumber ?? '-'}</TableCell>
                    <TableCell>{item.policyStatusCode ?? '-'}</TableCell>
                    <TableCell>{item.currentAgentCode ?? '-'}</TableCell>
                    <TableCell>{item.soldUnit ?? '-'}</TableCell>
                    <TableCell>{item.importedAgent ?? '-'}</TableCell>
                    <TableCell>{item.suggestedAgent ?? '-'}</TableCell>
                    <TableCell>{item._otherQualifiedAgent ?? '-'}</TableCell>
                    <TableCell>{item.selectedAgent ?? '-'}</TableCell>
                    <TableCell>{item.selectedAgentName ?? '-'}</TableCell>
                    <TableCell align="center">{item.isFirstPrioritySelectedAgent ? '✓' : '✕'}</TableCell>
                    <TableCell>{item.reasonSelectedAnotherAgent ?? '-'}</TableCell>
                    <TableCell>{item._policyTaskStatusLabel ?? '-'}</TableCell>
                    <TableCell align="right">
                      {item.actionedExpireAt ? formatDateTime(item.actionedExpireAt, 'DD/MM/YYYY') : '-'}
                    </TableCell>
                  </TableRow>
                );
              })}
              {paginatedItems.length === 0 && (
                <TableRow>
                  <TableCell colSpan={18} align="center">
                    <Typography variant="body2" color="textSecondary">
                      {t('opm_pd.noData')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredItems.length}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </div>
    </Paper>
  );
};

export default PolicyTable;
